/**
 * WP-V2-SG — GardenScene: Ogród marzeń (unlock + interakcje).
 * PLAN-V2 §6 Garden. Konsumuje: AnimatedDogAvatar, ObjectLayer, WorldCamera, WalkController.
 * Zachowuje unlock GARDEN_PRICE_ZL + sadzenie kwiatów.
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import type { CompanionLayerScene } from '../../companion/index.js';
import {
  AnimatedDogAvatar,
  OutfitStore,
  preloadAnchors,
} from '../../avatar/index.js';
import {
  ObjectLayer,
  playFx,
  type InteractEvent,
  type InteractTarget,
} from '../../interact/index.js';
import {
  setupWorld,
  follow,
  WalkController,
} from '../../core/world/index.js';
import {
  addFlowerLocal,
  bootstrapGarden,
  loadGardenFlowers,
  purchaseGarden,
  recordVisit,
} from './gardenApi.js';
import {
  FLOWER_CATALOG,
  GARDEN_PRICE_ZL,
  flowerTextureKey,
  priceLabelPl,
  unlockHintPl,
  type FlowerKind,
  type PlantedFlower,
} from './gardenState.js';
import {
  GARDEN_COLLIDERS,
  GARDEN_WORLD_H,
  GARDEN_WORLD_W,
  GARDEN_ROD_DEF_ID,
  GARDEN_STREAM_DEF_ID,
  GARDEN_LILY_DEF_ID,
  GARDEN_BASKET_DEF_ID,
  GARDEN_BUTTERFLY_DEF_ID,
  GARDEN_NET_DEF_ID,
  GARDEN_TEXTURE_SHAPES,
  FISH_COLORS,
  createFishingState,
  fishingStatusPl,
  fishTextureKey,
  gardenObjectDefs,
  gardenPlacements,
  gardenShapeForTexture,
  gardenSnapZones,
  gardenTextureKeys,
  lilyFrogJump,
  releaseFish,
  stepFishing,
  streamSplash,
  type FishingState,
  type GardenDecorKind,
  type FishKind,
} from './objects.js';
import {
  INTERACT_BUS_EVENT,
  gardenMissionFromCatch,
  isRodTapEvent,
} from './missionHook.js';

const GARDEN_TEX_SIZE = 160;

/**
 * Upewnij się, że wszystkie tekstury ogrodu istnieją.
 * PNG z Preload mają pierwszeństwo; brak → narysuj proceduralnie (bez emoji).
 */
export function ensureGardenTextures(scene: Phaser.Scene): void {
  for (const key of gardenTextureKeys()) {
    ensureGardenTexture(scene, key);
  }
  for (const def of gardenObjectDefs()) {
    ensureGardenTexture(scene, def.texture);
    if (def.states) {
      for (const s of def.states) {
        const stateKey = `obj_${def.id}_${s}`;
        if (!scene.textures.exists(stateKey)) {
          const kind =
            gardenShapeForTexture(def.texture) ??
            GARDEN_TEXTURE_SHAPES[def.texture];
          if (kind) {
            const g = scene.make.graphics({ x: 0, y: 0 });
            drawGardenShape(g, kind, GARDEN_TEX_SIZE, GARDEN_TEX_SIZE);
            g.generateTexture(stateKey, GARDEN_TEX_SIZE, GARDEN_TEX_SIZE);
            g.destroy();
          }
        }
      }
    }
  }
}

export function ensureGardenTexture(
  scene: Phaser.Scene,
  textureKey: string,
): string {
  if (scene.textures.exists(textureKey)) return textureKey;
  const kind = gardenShapeForTexture(textureKey);
  if (!kind) return textureKey;
  const g = scene.make.graphics({ x: 0, y: 0 });
  drawGardenShape(g, kind, GARDEN_TEX_SIZE, GARDEN_TEX_SIZE);
  g.generateTexture(textureKey, GARDEN_TEX_SIZE, GARDEN_TEX_SIZE);
  g.destroy();
  return textureKey;
}

export function drawGardenShape(
  g: Phaser.GameObjects.Graphics,
  kind: GardenDecorKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;
  switch (kind) {
    case 'rod':
      drawRod(g, cx, cy);
      break;
    case 'stream':
      drawStream(g, cx, cy);
      break;
    case 'lily':
      drawLily(g, cx, cy);
      break;
    case 'blanket':
      drawBlanket(g, cx, cy);
      break;
    case 'basket':
      drawBasket(g, cx, cy);
      break;
    case 'treat':
      drawTreat(g, cx, cy);
      break;
    case 'butterfly':
      drawButterfly(g, cx, cy, 0xce93d8);
      break;
    case 'net':
      drawNet(g, cx, cy);
      break;
    case 'waterfall':
      drawWaterfall(g, cx, cy);
      break;
    case 'fish_gold':
      drawFish(g, cx, cy, FISH_COLORS.gold);
      break;
    case 'fish_blue':
      drawFish(g, cx, cy, FISH_COLORS.blue);
      break;
    case 'fish_pink':
      drawFish(g, cx, cy, FISH_COLORS.pink);
      break;
    case 'fish_green':
      drawFish(g, cx, cy, FISH_COLORS.green);
      break;
    case 'frog':
      drawFrog(g, cx, cy);
      break;
    case 'fountain':
      drawFountain(g, cx, cy);
      break;
    case 'trampoline':
      drawGardenTrampoline(g, cx, cy);
      break;
    case 'swing':
      drawSwing(g, cx, cy);
      break;
    case 'cat':
      drawCat(g, cx, cy);
      break;
    case 'lock':
      drawLock(g, cx, cy);
      break;
    case 'flower_rose':
      drawFlower(g, cx, cy, 0xf48fb1, 'rose');
      break;
    case 'flower_tulip':
      drawFlower(g, cx, cy, 0xef9a9a, 'tulip');
      break;
    case 'flower_daisy':
      drawFlower(g, cx, cy, 0xfff59d, 'daisy');
      break;
    case 'flower_sunflower':
      drawFlower(g, cx, cy, 0xffd54f, 'sunflower');
      break;
  }
}

function drawRod(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.lineStyle(6, 0x8d6e63, 1);
  g.lineBetween(cx - 40, cy + 40, cx + 36, cy - 48);
  g.lineStyle(2, 0xb0bec5, 1);
  g.lineBetween(cx + 36, cy - 48, cx + 50, cy + 20);
  g.fillStyle(0xff8a65, 1);
  g.fillCircle(cx + 50, cy + 24, 6);
  g.fillStyle(0xa1887f, 1);
  g.fillRoundedRect(cx - 48, cy + 32, 20, 14, 4);
  g.fillStyle(0x81c784, 0.45);
  g.fillEllipse(cx, cy + 52, 70, 14);
}

function drawStream(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0x4fc3f7, 0.95);
  g.fillEllipse(cx, cy + 8, 120, 48);
  g.fillStyle(0x81d4fa, 0.9);
  g.fillEllipse(cx - 10, cy, 90, 32);
  g.fillStyle(0xffffff, 0.5);
  g.fillEllipse(cx - 20, cy - 6, 30, 10);
  g.fillStyle(0x29b6f6, 0.7);
  g.fillCircle(cx + 24, cy + 4, 8);
  g.fillCircle(cx - 30, cy + 10, 6);
}

function drawLily(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0x66bb6a, 1);
  g.fillEllipse(cx, cy + 16, 90, 36);
  g.fillStyle(0x81c784, 1);
  g.fillEllipse(cx - 16, cy + 10, 40, 24);
  g.fillStyle(0xf8bbd0, 1);
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI * 2 * i) / 6;
    g.fillEllipse(cx + Math.cos(a) * 14, cy - 8 + Math.sin(a) * 10, 16, 12);
  }
  g.fillStyle(0xfff59d, 1);
  g.fillCircle(cx, cy - 8, 10);
}

function drawBlanket(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xffcc80, 1);
  g.fillRoundedRect(cx - 54, cy - 36, 108, 72, 8);
  g.fillStyle(0xff8a80, 1);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      if ((r + c) % 2 === 0) {
        g.fillRect(cx - 48 + c * 32, cy - 30 + r * 22, 28, 18);
      }
    }
  }
  g.lineStyle(3, 0xffffff, 0.9);
  g.strokeRoundedRect(cx - 54, cy - 36, 108, 72, 8);
}

function drawBasket(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xd7a86e, 1);
  g.fillRoundedRect(cx - 36, cy - 12, 72, 48, 10);
  g.fillStyle(0xffe082, 1);
  g.fillEllipse(cx, cy - 12, 76, 28);
  g.lineStyle(5, 0xa1887f, 1);
  g.strokeCircle(cx, cy - 36, 22);
  g.fillStyle(0xffab91, 1);
  g.fillCircle(cx - 10, cy - 8, 8);
  g.fillStyle(0xa5d6a7, 1);
  g.fillCircle(cx + 12, cy - 4, 7);
  g.lineStyle(3, 0xffffff, 0.7);
  g.strokeRoundedRect(cx - 36, cy - 12, 72, 48, 10);
}

function drawTreat(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xffb74d, 1);
  g.fillRoundedRect(cx - 28, cy - 16, 56, 36, 10);
  g.fillStyle(0xffe0b2, 1);
  g.fillCircle(cx - 8, cy - 2, 6);
  g.fillCircle(cx + 10, cy + 4, 5);
  g.fillStyle(0xef9a9a, 1);
  g.fillCircle(cx + 4, cy - 8, 4);
  g.lineStyle(3, 0xffffff, 0.85);
  g.strokeRoundedRect(cx - 28, cy - 16, 56, 36, 10);
}

function drawButterfly(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  color: number,
): void {
  g.fillStyle(color, 1);
  g.fillEllipse(cx - 18, cy - 6, 36, 48);
  g.fillEllipse(cx + 18, cy - 6, 36, 48);
  g.fillStyle(0xffffff, 0.55);
  g.fillEllipse(cx - 18, cy - 10, 14, 18);
  g.fillEllipse(cx + 18, cy - 10, 14, 18);
  g.fillStyle(0x5d4037, 1);
  g.fillRoundedRect(cx - 3, cy - 20, 6, 44, 3);
  g.fillCircle(cx, cy - 24, 5);
}

function drawNet(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.lineStyle(6, 0x8d6e63, 1);
  g.lineBetween(cx - 30, cy + 48, cx + 20, cy - 10);
  g.lineStyle(3, 0x90caf9, 0.95);
  g.strokeCircle(cx + 28, cy - 24, 28);
  g.lineStyle(2, 0xb3e5fc, 0.8);
  g.lineBetween(cx + 10, cy - 24, cx + 46, cy - 24);
  g.lineBetween(cx + 28, cy - 42, cx + 28, cy - 6);
  g.lineBetween(cx + 14, cy - 36, cx + 42, cy - 12);
}

function drawWaterfall(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0x90a4ae, 1);
  g.fillRoundedRect(cx - 40, cy - 48, 80, 24, 6);
  g.fillStyle(0x81d4fa, 0.95);
  g.fillRect(cx - 24, cy - 28, 48, 70);
  g.fillStyle(0xffffff, 0.55);
  g.fillRect(cx - 16, cy - 24, 10, 60);
  g.fillStyle(0xb3e5fc, 0.9);
  g.fillEllipse(cx, cy + 46, 70, 20);
  g.fillStyle(0xffab91, 0.6);
  g.fillCircle(cx - 20, cy - 10, 6);
  g.fillCircle(cx + 18, cy, 5);
  g.fillCircle(cx, cy - 20, 4);
}

function drawFish(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  color: number,
): void {
  g.fillStyle(color, 1);
  g.fillEllipse(cx - 4, cy, 70, 36);
  g.fillTriangle(cx + 28, cy, cx + 52, cy - 18, cx + 52, cy + 18);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(cx - 22, cy - 4, 6);
  g.fillStyle(0x37474f, 1);
  g.fillCircle(cx - 22, cy - 4, 3);
  g.fillStyle(color, 0.85);
  g.fillTriangle(cx - 4, cy - 16, cx + 10, cy - 34, cx + 16, cy - 12);
}

function drawFrog(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0x66bb6a, 1);
  g.fillEllipse(cx, cy + 6, 70, 48);
  g.fillStyle(0x81c784, 1);
  g.fillCircle(cx - 18, cy - 16, 14);
  g.fillCircle(cx + 18, cy - 16, 14);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(cx - 18, cy - 16, 6);
  g.fillCircle(cx + 18, cy - 16, 6);
  g.fillStyle(0x37474f, 1);
  g.fillCircle(cx - 18, cy - 16, 3);
  g.fillCircle(cx + 18, cy - 16, 3);
  g.fillStyle(0xa5d6a7, 1);
  g.fillEllipse(cx, cy + 14, 36, 18);
}

function drawFountain(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xb0bec5, 1);
  g.fillEllipse(cx, cy + 28, 100, 36);
  g.fillStyle(0x90a4ae, 1);
  g.fillRoundedRect(cx - 14, cy - 20, 28, 48, 6);
  g.fillStyle(0x4fc3f7, 0.95);
  g.fillCircle(cx, cy - 28, 14);
  g.fillStyle(0x81d4fa, 0.85);
  g.fillCircle(cx - 18, cy - 40, 7);
  g.fillCircle(cx + 16, cy - 46, 6);
  g.fillCircle(cx, cy - 52, 5);
  g.fillStyle(0xffffff, 0.5);
  g.fillEllipse(cx - 10, cy + 22, 30, 10);
}

function drawGardenTrampoline(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  g.fillStyle(0x5d4037, 1);
  g.fillEllipse(cx, cy + 16, 100, 34);
  g.fillStyle(0xab47bc, 1);
  g.fillEllipse(cx, cy + 6, 86, 26);
  g.fillStyle(0xce93d8, 0.9);
  g.fillEllipse(cx, cy + 2, 60, 16);
  g.lineStyle(3, 0xffffff, 0.85);
  g.strokeEllipse(cx, cy + 6, 86, 26);
}

function drawSwing(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.lineStyle(8, 0x6d4c41, 1);
  g.lineBetween(cx - 40, cy + 40, cx, cy - 48);
  g.lineBetween(cx + 40, cy + 40, cx, cy - 48);
  g.lineStyle(5, 0x8d6e63, 1);
  g.lineBetween(cx - 28, cy - 36, cx + 28, cy - 36);
  g.lineStyle(3, 0xb0bec5, 1);
  g.lineBetween(cx - 14, cy - 36, cx - 14, cy + 12);
  g.lineBetween(cx + 14, cy - 36, cx + 14, cy + 12);
  g.fillStyle(0xff8a65, 1);
  g.fillRoundedRect(cx - 24, cy + 8, 48, 16, 5);
  g.fillStyle(0xa5d6a7, 0.5);
  g.fillEllipse(cx, cy + 48, 70, 14);
}

function drawCat(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xffe0b2, 1);
  g.fillCircle(cx, cy + 4, 36);
  g.fillTriangle(cx - 28, cy - 16, cx - 10, cy - 40, cx - 4, cy - 12);
  g.fillTriangle(cx + 28, cy - 16, cx + 10, cy - 40, cx + 4, cy - 12);
  g.fillStyle(0xffcc80, 1);
  g.fillTriangle(cx - 22, cy - 16, cx - 12, cy - 30, cx - 8, cy - 12);
  g.fillTriangle(cx + 22, cy - 16, cx + 12, cy - 30, cx + 8, cy - 12);
  g.fillStyle(0x5d4037, 1);
  g.fillCircle(cx - 12, cy, 5);
  g.fillCircle(cx + 12, cy, 5);
  g.fillStyle(0xf48fb1, 1);
  g.fillTriangle(cx, cy + 6, cx - 6, cy + 14, cx + 6, cy + 14);
  g.lineStyle(2, 0x8d6e63, 1);
  g.lineBetween(cx - 10, cy + 10, cx - 36, cy + 4);
  g.lineBetween(cx + 10, cy + 10, cx + 36, cy + 4);
}

function drawLock(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.lineStyle(10, 0x90a4ae, 1);
  g.strokeCircle(cx, cy - 18, 22);
  g.fillStyle(0xffd54f, 1);
  g.fillRoundedRect(cx - 32, cy - 8, 64, 52, 10);
  g.fillStyle(0xfff8e1, 1);
  g.fillCircle(cx, cy + 12, 10);
  g.fillStyle(0xffb300, 1);
  g.fillRect(cx - 4, cy + 14, 8, 16);
  g.lineStyle(3, 0xffffff, 0.8);
  g.strokeRoundedRect(cx - 32, cy - 8, 64, 52, 10);
}

function drawFlower(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  color: number,
  style: 'rose' | 'tulip' | 'daisy' | 'sunflower',
): void {
  // łodyga + liść
  g.lineStyle(5, 0x66bb6a, 1);
  g.lineBetween(cx, cy + 48, cx, cy + 4);
  g.fillStyle(0x81c784, 1);
  g.fillEllipse(cx - 14, cy + 24, 22, 10);
  if (style === 'tulip') {
    g.fillStyle(color, 1);
    g.fillEllipse(cx, cy - 8, 36, 44);
    g.fillStyle(0xffffff, 0.35);
    g.fillEllipse(cx - 6, cy - 16, 12, 18);
    g.fillStyle(0x66bb6a, 1);
    g.fillRect(cx - 3, cy + 12, 6, 10);
  } else if (style === 'sunflower') {
    g.fillStyle(color, 1);
    for (let i = 0; i < 10; i++) {
      const a = (Math.PI * 2 * i) / 10;
      g.fillEllipse(cx + Math.cos(a) * 22, cy - 4 + Math.sin(a) * 22, 16, 12);
    }
    g.fillStyle(0x6d4c41, 1);
    g.fillCircle(cx, cy - 4, 16);
  } else if (style === 'rose') {
    g.fillStyle(color, 1);
    g.fillCircle(cx, cy - 6, 22);
    g.fillStyle(0xf8bbd0, 1);
    g.fillCircle(cx - 8, cy - 12, 12);
    g.fillCircle(cx + 10, cy - 4, 10);
    g.fillStyle(0xffffff, 0.35);
    g.fillCircle(cx - 4, cy - 14, 6);
  } else {
    // daisy
    g.fillStyle(0xffffff, 1);
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI * 2 * i) / 8;
      g.fillEllipse(cx + Math.cos(a) * 18, cy - 4 + Math.sin(a) * 18, 16, 12);
    }
    g.fillStyle(color, 1);
    g.fillCircle(cx, cy - 4, 12);
  }
  g.fillStyle(0x81c784, 0.4);
  g.fillEllipse(cx, cy + 52, 40, 10);
}

export class GardenScene extends BaseScene {
  static readonly KEY = 'Garden';

  private unlocked = false;
  private balanceZl = 0;
  private flowers: PlantedFlower[] = [];
  private selectedFlower: FlowerKind = 'daisy';
  private root: Phaser.GameObjects.Container | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private balanceText: Phaser.GameObjects.Text | null = null;
  private flowerLayer: Phaser.GameObjects.Container | null = null;
  private butterflies: Phaser.GameObjects.Image[] = [];
  private buying = false;
  private plantMode = false;
  private bgImage: Phaser.GameObjects.Image | null = null;

  private avatar: AnimatedDogAvatar | null = null;
  private walk: WalkController | null = null;
  private layer: ObjectLayer | null = null;
  private unsubLayer: (() => void) | null = null;
  private fishing: FishingState = createFishingState();
  private fishFx: Phaser.GameObjects.GameObject[] = [];
  private frogNode: Phaser.GameObjects.Image | null = null;
  private holdingNet = false;

  constructor() {
    super(GardenScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_garden');
    this.preloadRealBg('bg_garden_gate');
    for (const pose of ['idle', 'walkA', 'walkB', 'sit', 'jump', 'wave'] as const) {
      const key = `dog_maltese_${pose}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/maltese_${pose}.png`);
      }
    }
    if (!this.textures.exists('ph_dog')) {
      this.load.image('ph_dog', '/assets/placeholder/dog.png');
    }
    void preloadAnchors(this);
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0xa5d6a7);
    this.bgImage = this.addRealBg('bg_garden_gate');
    this.root = this.add.container(0, 0);
    this.statusText = this.add
      .text(this.gameW / 2, this.gameH - 24, 'Ładowanie ogrodu…', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
      })
      .setOrigin(0.5)
      .setDepth(200)
      .setScrollFactor(0);

    void this.bootstrap();
    this.events.once('shutdown', () => this.teardown());
    this.events.once('destroy', () => this.teardown());
  }

  update(_t: number, dtMs: number): void {
    if (!this.unlocked) return;
    const dt = Math.min(0.05, dtMs / 1000);
    this.walk?.update(dt);
    const pos = this.walk?.position;
    this.avatar?.update(dt);
    if (pos && this.avatar) {
      this.avatar.container.setPosition(pos.x, pos.y);
    }
    this.layer?.update(dt);
  }

  private teardown(): void {
    this.teardownEngines();
    this.butterflies = [];
    this.input.off('pointerdown');
  }

  private teardownEngines(): void {
    this.unsubLayer?.();
    this.unsubLayer = null;
    this.layer?.destroy();
    this.layer = null;
    this.walk?.destroy();
    this.walk = null;
    this.avatar?.destroy();
    this.avatar = null;
    for (const n of this.fishFx) n.destroy();
    this.fishFx = [];
    this.frogNode?.destroy();
    this.frogNode = null;
    this.registry.remove('companionFollow');
  }

  private async bootstrap(): Promise<void> {
    const state = await bootstrapGarden();
    if (!this.sys.isActive()) return;
    this.unlocked = state.unlocked;
    this.balanceZl = state.balanceZl;
    this.flowers = state.flowers.length ? state.flowers : loadGardenFlowers();

    void recordVisit(this.unlocked ? 'garden' : 'garden-gate');
    if (!this.unlocked) this.companion()?.notifyLockedPlace();
    this.repaint();
  }

  private companion(): CompanionLayerScene | null {
    const s = this.game.scene.getScene('CompanionLayer');
    return s ? (s as unknown as CompanionLayerScene) : null;
  }

  private repaint(): void {
    this.teardownEngines();
    this.root?.removeAll(true);
    this.flowerLayer = null;
    this.butterflies = [];
    this.input.off('pointerdown');
    this.fishing = createFishingState();
    this.holdingNet = false;

    this.cameras.main.stopFollow();
    this.cameras.main.setScroll(0, 0);
    this.cameras.main.setBounds(0, 0, this.gameW, this.gameH);

    const bgId = this.unlocked ? 'bg_garden' : 'bg_garden_gate';
    if (this.bgImage && this.textures.exists(bgId)) {
      this.bgImage.setTexture(bgId).setDisplaySize(this.gameW, this.gameH);
      this.bgImage.setScrollFactor(0);
    }

    if (this.unlocked) {
      this.paintUnlocked();
    } else {
      this.paintLocked();
    }
  }

  // ── Zablokowany (brama + cena) — bez regresji ───────────────────────

  private paintLocked(): void {
    try {
      ensureGardenTextures(this);
    } catch {
      /* headless */
    }
    this.drawPreviewAttractions(0.45);

    const gateY = 360;
    const fence = this.add
      .rectangle(this.gameW / 2, gateY, this.gameW - 80, 28, 0x8d6e63)
      .setStrokeStyle(3, 0x5d4037);
    const gate = this.add
      .rectangle(this.gameW / 2, gateY - 40, 160, 100, 0xa1887f)
      .setStrokeStyle(4, 0xffffff);
    const lock = this.add
      .image(this.gameW / 2, gateY - 40, 'obj_garden_lock')
      .setDisplaySize(56, 56);

    const title = this.add
      .text(this.gameW / 2, 36, 'Ogród marzeń', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '36px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const sign = this.add
      .rectangle(this.gameW / 2, 140, 320, MIN_TOUCH + 24, 0xfff8e1)
      .setStrokeStyle(4, 0xffd54f);
    const price = this.add
      .text(this.gameW / 2, 140, priceLabelPl(GARDEN_PRICE_ZL), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        color: '#5d4037',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.balanceText = this.add
      .text(this.gameW / 2, 200, `Twoje zł: ${this.balanceZl}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
      })
      .setOrigin(0.5);

    const cat = this.add
      .circle(200, 520, 40, 0xffe0b2)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const catFace = this.add
      .image(200, 520, 'obj_garden_cat')
      .setDisplaySize(64, 64);
    const bubble = this.add
      .rectangle(480, 500, 420, 100, 0xffffff, 0.95)
      .setStrokeStyle(3, 0xffb6c1);
    const tip = this.add
      .text(480, 500, `Kotek: ${unlockHintPl()}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        wordWrap: { width: 380 },
        align: 'center',
      })
      .setOrigin(0.5);

    cat.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event?.stopPropagation?.();
      this.tweens.add({
        targets: catFace,
        scale: 1.25,
        duration: 140,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
      const heart = this.add
        .circle(200, 490, 10, 0xff8a80)
        .setStrokeStyle(2, 0xffffff)
        .setDepth(300);
      this.root?.add(heart);
      this.tweens.add({
        targets: heart,
        y: 440,
        alpha: 0,
        duration: 700,
        onComplete: () => heart.destroy(),
      });
      this.setStatus('Kotek mruczy — zbieraj złote monety w miasteczku!');
    });

    this.root?.add([
      fence,
      gate,
      lock,
      title,
      sign,
      price,
      this.balanceText,
      cat,
      catFace,
      bubble,
      tip,
    ]);

    const buy = UiKit.button(this, {
      x: this.gameW / 2,
      y: 280,
      label: `Otwórz ogród · ${priceLabelPl()}`,
      fill: 0xff9ec8,
      width: 360,
      onClick: () => {
        void this.tryBuy();
      },
    });
    this.root?.add(buy);

    const back = UiKit.button(this, {
      x: this.gameW / 2,
      y: this.gameH - 70,
      label: 'Wróć do miasteczka',
      fill: 0xffcc80,
      width: 280,
      onClick: () => getSceneRouter().go('Town'),
    });
    this.root?.add(back);

    this.setStatus('Podgląd zza bramy — zbierz złote monety i wejdź!');
  }

  private async tryBuy(): Promise<void> {
    if (this.buying || this.unlocked) return;
    this.buying = true;
    this.setStatus('Otwieramy bramę… ✨');

    const result = await purchaseGarden(this.balanceZl, this.unlocked);
    if (!this.sys.isActive()) return;

    if (result.ok) {
      this.unlocked = true;
      this.balanceZl = result.balanceZl;
      this.setStatus('Ogród marzeń otwarty! 🌈');
      void recordVisit('garden');
      this.repaint();
      this.buying = false;
      return;
    }

    if (result.code === 'INSUFFICIENT_FUNDS' || result.code === 'funds') {
      this.setStatus(
        `Jeszcze trochę złotych monet… brakuje do ${priceLabelPl()}. Kotek pomoże zarobić!`,
      );
      this.companion()?.notifyServerDenied();
    } else if (result.code === 'ALREADY') {
      this.unlocked = true;
      this.repaint();
    } else if (result.code === 'OFFLINE') {
      this.setStatus('Poczekaj na internet — bramę otworzymy, gdy wróci sieć. 🌈');
    } else {
      this.setStatus('Spróbujmy za chwilę — ogród na Ciebie czeka! 💚');
    }
    this.buying = false;
  }

  // ── Odblokowany — silniki + atrakcje ────────────────────────────────

  private paintUnlocked(): void {
    setupWorld(this, {
      worldW: GARDEN_WORLD_W,
      worldH: GARDEN_WORLD_H,
      bg: 0xa5d6a7,
    });

    // PNG z preload; gdy brak — proceduralne kształty (bez emoji-fallbacków)
    try {
      ensureGardenTextures(this);
    } catch {
      /* headless */
    }

    // szerokie tło świata
    if (this.textures.exists('bg_garden')) {
      this.add
        .image(GARDEN_WORLD_W / 2, GARDEN_WORLD_H / 2, 'bg_garden')
        .setDisplaySize(GARDEN_WORLD_W, GARDEN_WORLD_H)
        .setDepth(-1000);
    }

    this.drawStreamDecor();
    this.drawLegacyAttractions();
    this.spawnPlayer();
    this.spawnObjectLayer();
    this.flowerLayer = this.add.container(0, 0).setDepth(30);
    this.root?.add(this.flowerLayer);
    this.paintFlowers();
    this.spawnButterflies();
    this.paintFlowerPalette();

    const title = this.add
      .text(this.gameW / 2, 28, 'Ogród marzeń', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '34px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);
    this.root?.add(title);

    const back = UiKit.button(this, {
      x: 140,
      y: this.gameH - 70,
      label: 'Miasteczko',
      fill: 0xffcc80,
      width: 200,
      onClick: () => getSceneRouter().go('Town'),
    });
    back.setScrollFactor(0);
    back.setDepth(20_100);
    this.root?.add(back);

    this.setStatus('Wędka · rzeczka · lilie · piknik · motyle · kwiatki 🌸');
    this.registry.set('companionFollow', {
      x: GARDEN_WORLD_W / 2,
      y: 420,
    });

    // sadzenie: tap w pole kwiatów (zachowany system)
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (!this.plantMode) return;
      const wy = p.worldY ?? p.y;
      const wx = p.worldX ?? p.x;
      if (wy < 520 || wy > 640) return;
      if (wx < 80 || wx > GARDEN_WORLD_W - 80) return;
      this.flowers = addFlowerLocal(this.flowers, {
        kind: this.selectedFlower,
        x: wx,
        y: wy,
      });
      this.paintFlowers();
      const planted = FLOWER_CATALOG.find((f) => f.kind === this.selectedFlower);
      this.setStatus(`Posadzono: ${planted?.namePl ?? 'kwiatek'} — pięknie!`);
    });
  }

  private drawStreamDecor(): void {
    // rzeczka wizualna (pas wody)
    const stream = this.add
      .rectangle(520, 430, 520, 90, 0x4fc3f7, 0.55)
      .setStrokeStyle(3, 0xffffff)
      .setDepth(2);
    const pond = this.add
      .ellipse(720, 420, 200, 100, 0x29b6f6, 0.5)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(2);
    this.root?.add([stream, pond]);
  }

  private drawLegacyAttractions(): void {
    // fontanna + trampolina + huśtawka (jak WP9) — cele ≥64
    const items = this.buildAttractionObjects();
    for (const o of items) this.root?.add(o);
  }

  private spawnPlayer(): void {
    const cache = OutfitStore.load();
    const breed =
      cache.breed || (this.registry.get('dogBreed') as string) || 'maltese';
    const color =
      cache.color || (this.registry.get('dogColor') as string) || undefined;
    this.avatar = new AnimatedDogAvatar({
      scene: this,
      x: 200,
      y: 500,
      breed,
      color,
      slots: cache.slots,
      swimSlots: cache.swimSlots,
      mode: 'normal',
      depth: 100,
    });
    this.walk = new WalkController(this, this.avatar, {
      colliders: GARDEN_COLLIDERS,
      bounds: {
        minX: 48,
        minY: 120,
        maxX: GARDEN_WORLD_W - 48,
        maxY: GARDEN_WORLD_H - 48,
      },
      enableInput: true,
    });
    follow(this, this.avatar.container, {
      lerp: 0.12,
      deadzone: { width: 120, height: 80 },
    });
  }

  private spawnObjectLayer(): void {
    const self = this;
    const playerTarget: InteractTarget = {
      moveTo: (x, y) => {
        self.walk?.moveTo(x, y);
      },
      holdItem: (tex) => {
        self.avatar?.holdItem(tex);
      },
      playAction: (a) => {
        if (!self.avatar) return Promise.resolve();
        if (a === 'sniff') return self.avatar.playAction('happy');
        if (a === 'swim') return self.avatar.playAction('jump');
        const known = a as 'sit' | 'jump' | 'wave' | 'eat' | 'swim' | 'sleep' | 'happy';
        return self.avatar.playAction(known);
      },
      getPosition: () =>
        self.walk?.position ?? { x: self.avatar?.x ?? 0, y: self.avatar?.y ?? 0 },
      get isMoving() {
        return self.walk?.isMoving ?? false;
      },
      get x() {
        return self.walk?.position.x ?? self.avatar?.x ?? 0;
      },
      get y() {
        return self.walk?.position.y ?? self.avatar?.y ?? 0;
      },
    };

    this.layer = new ObjectLayer({
      scene: this,
      defs: gardenObjectDefs(),
      target: playerTarget,
      interactRange: 100,
      groundBounds: {
        minX: 48,
        maxX: GARDEN_WORLD_W - 48,
        minY: 360,
        maxY: GARDEN_WORLD_H - 48,
      },
    });
    this.layer.setSnapZones(gardenSnapZones());
    this.layer.spawn(gardenPlacements());
    // G9 / Toca Boca: zero widocznego tekstu na obiektach — hotspot + lektor, bez podpisów.
    this.unsubLayer = this.layer.on((evt) => this.onInteract(evt));
  }

  private onInteract(evt: InteractEvent): void {
    this.game.events.emit(INTERACT_BUS_EVENT, {
      sceneKey: GardenScene.KEY,
      ...evt,
    });
    this.events.emit('interact', evt);

    if (isRodTapEvent(evt)) {
      this.advanceFishing(evt);
      return;
    }

    if (evt.type === 'tap' || evt.type === 'toggle') {
      if (evt.defId === GARDEN_STREAM_DEF_ID) {
        const r = streamSplash(evt.defId);
        if (r.splash) {
          playFx(this, 'splash', evt.x, evt.y);
          void this.avatar?.playAction('jump');
          this.setStatus('Plusk w rzeczce! 🌊');
          // piesek „pływa" chwilę
          this.tweens.add({
            targets: this.avatar?.container,
            y: (this.avatar?.container.y ?? evt.y) + 8,
            duration: 200,
            yoyo: true,
            repeat: 2,
          });
        }
      }
      if (evt.defId === GARDEN_LILY_DEF_ID) {
        const r = lilyFrogJump(evt.defId);
        if (r.frog) this.playFrogJump(evt.x, evt.y);
      }
      if (evt.defId === GARDEN_BASKET_DEF_ID) {
        playFx(this, 'crumbs', evt.x, evt.y);
        this.setStatus('Koszyk piknikowy — smakołyki! 🍪');
      }
      if (evt.defId === 'garden_waterfall') {
        playFx(this, 'splash', evt.x, evt.y);
        playFx(this, 'stars', evt.x, evt.y - 40);
        this.setStatus('Tęcza pod wodospadem! 🌈');
      }
      if (evt.defId === GARDEN_BUTTERFLY_DEF_ID) {
        this.catchButterflyAt(evt.x, evt.y);
      }
      if (evt.defId === GARDEN_NET_DEF_ID) {
        this.holdingNet = true;
        this.setStatus('Siatka w łapkach — łap motyle! 🕸️');
      }
      if (evt.defId === 'garden_blanket') {
        void this.avatar?.playAction('sit');
        playFx(this, 'hearts', evt.x, evt.y);
        this.setStatus('Piknik na kocu! 🧺');
      }
    }

    if (evt.type === 'pickup' && evt.defId === GARDEN_NET_DEF_ID) {
      this.holdingNet = true;
    }
    if (evt.type === 'drop' && evt.defId === GARDEN_NET_DEF_ID) {
      this.holdingNet = false;
    }
  }

  /**
   * Pełny cykl wędki na tap: idle→cast→bite→reel→caught→idle.
   * Po złowieniu emituje misję garden_fish.
   */
  private advanceFishing(evt: InteractEvent): void {
    // auto-advance cast→bite po krótkim czasie w UI; tu deterministycznie w jednym tap
    // Model: każdy tap na wędce = jeden krok maszyny (test E2E unit pokrywa cykl).
    const prev = this.fishing.phase;
    this.fishing = stepFishing(this.fishing, 'tap');

    // Po zarzuceniu od razu „rybka bierze" (krótki tween), by dziecko nie czekało
    if (this.fishing.phase === 'cast') {
      this.setStatus(fishingStatusPl(this.fishing));
      playFx(this, 'splash', evt.x + 40, evt.y + 20);
      this.time.delayedCall(450, () => {
        if (!this.sys.isActive()) return;
        if (this.fishing.phase === 'cast') {
          this.fishing = stepFishing(this.fishing, 'tap'); // → bite
          this.setStatus(fishingStatusPl(this.fishing));
          playFx(this, 'sparkle', evt.x + 50, evt.y + 30);
        }
      });
      return;
    }

    if (this.fishing.phase === 'bite') {
      this.setStatus(fishingStatusPl(this.fishing));
      return;
    }

    if (this.fishing.phase === 'reel') {
      this.setStatus(fishingStatusPl(this.fishing));
      // auto finish reel → caught
      this.time.delayedCall(300, () => {
        if (!this.sys.isActive()) return;
        if (this.fishing.phase === 'reel') {
          this.fishing = stepFishing(this.fishing, 'tap');
          this.onFishCaught(evt);
        }
      });
      return;
    }

    if (this.fishing.phase === 'caught') {
      this.onFishCaught(evt);
      return;
    }

    if (this.fishing.phase === 'idle' && prev === 'caught') {
      this.setStatus('Rybka wróciła do wody — zarzuć znowu! 🎣');
    } else {
      this.setStatus(fishingStatusPl(this.fishing));
    }
  }

  private onFishCaught(evt: InteractEvent): void {
    const kind = this.fishing.lastCaught;
    if (!kind) return;
    const tex = fishTextureKey(kind as FishKind);
    ensureGardenTexture(this, tex);
    const fish = this.add
      .image(evt.x + 30, evt.y - 20, tex)
      .setDisplaySize(48, 48)
      .setDepth(7000);
    this.fishFx.push(fish);
    this.tweens.add({
      targets: fish,
      y: evt.y - 80,
      duration: 600,
      yoyo: true,
      onComplete: () => {
        // wraca do wody
        this.tweens.add({
          targets: fish,
          x: evt.x + 80,
          y: evt.y + 40,
          alpha: 0,
          duration: 500,
          onComplete: () => {
            fish.destroy();
            this.fishFx = this.fishFx.filter((n) => n !== fish);
          },
        });
      },
    });
    playFx(this, 'stars', evt.x, evt.y - 40);
    playFx(this, 'splash', evt.x + 40, evt.y + 20);
    this.setStatus(fishingStatusPl(this.fishing));

    const mission = gardenMissionFromCatch(kind, evt);
    this.game.events.emit(INTERACT_BUS_EVENT, mission);
    this.events.emit('quest-progress', mission);

    // po chwili release
    this.time.delayedCall(1200, () => {
      if (!this.sys.isActive()) return;
      if (this.fishing.phase === 'caught') {
        this.fishing = releaseFish(this.fishing);
        this.setStatus('Rybka wróciła do wody — zarzuć znowu! 🎣');
      }
    });
  }

  private playFrogJump(x: number, y: number): void {
    this.frogNode?.destroy();
    ensureGardenTexture(this, 'obj_garden_frog');
    this.frogNode = this.add
      .image(x, y - 10, 'obj_garden_frog')
      .setDisplaySize(48, 48)
      .setDepth(6000);
    this.tweens.add({
      targets: this.frogNode,
      x: x + 60,
      y: y - 50,
      duration: 280,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.frogNode?.destroy();
        this.frogNode = null;
      },
    });
    playFx(this, 'sparkle', x, y);
    this.setStatus('Żabka skacze z lilii!');
  }

  private catchButterflyAt(x: number, y: number): void {
    playFx(this, 'stars', x, y);
    this.setStatus(
      this.holdingNet
        ? 'Złapałeś motylka siatką! 🦋'
        : 'Złapałeś motylka! 🦋 (wróci za chwilę)',
    );
  }

  private drawPreviewAttractions(alpha: number): void {
    const items = this.buildAttractionObjects();
    for (const o of items) {
      const anyO = o as Phaser.GameObjects.GameObject & { setAlpha?: (a: number) => void };
      anyO.setAlpha?.(alpha);
      this.root?.add(o);
    }
  }

  private buildAttractionObjects(): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    ensureGardenTexture(this, 'obj_garden_fountain');
    ensureGardenTexture(this, 'obj_garden_trampoline');
    ensureGardenTexture(this, 'obj_garden_swing');

    const fx = 280;
    const fy = 280;
    const fountain = this.add
      .image(fx, fy, 'obj_garden_fountain')
      .setDisplaySize(96, 96)
      .setInteractive({ useHandCursor: true });
    fountain.on('pointerdown', () => this.playFountain(fx, fy));
    out.push(fountain);

    const tx = 640;
    const ty = 300;
    const tramp = this.add
      .image(tx, ty, 'obj_garden_trampoline')
      .setDisplaySize(100, 100)
      .setInteractive({ useHandCursor: true });
    tramp.on('pointerdown', () => {
      this.tweens.add({
        targets: tramp,
        y: ty - 56,
        duration: 280,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
      void this.avatar?.playAction('jump');
      this.setStatus('Hop!');
    });
    out.push(tramp);

    const sx = 1680;
    const sy = 290;
    const swing = this.add
      .image(sx, sy, 'obj_garden_swing')
      .setDisplaySize(88, 88)
      .setInteractive({ useHandCursor: true });
    swing.on('pointerdown', () => {
      this.tweens.add({
        targets: swing,
        x: sx + 30,
        duration: 350,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          swing.setPosition(sx, sy);
        },
      });
      this.setStatus('Huśtaj się!');
    });
    out.push(swing);

    const bed = this.add
      .rectangle(GARDEN_WORLD_W / 2, 580, GARDEN_WORLD_W - 200, 100, 0x8d6e63, 0.45)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: true });
    out.push(bed);

    return out;
  }

  private playFountain(x: number, y: number): void {
    const colors = [0xff8a80, 0xfff176, 0x80d8ff, 0xce93d8, 0xa5d6a7];
    for (let i = 0; i < 8; i++) {
      const drop = this.add
        .circle(x, y - 20, 8, colors[i % colors.length])
        .setDepth(50);
      this.tweens.add({
        targets: drop,
        x: x + (Math.random() - 0.5) * 120,
        y: y - 80 - Math.random() * 40,
        alpha: 0,
        duration: 700,
        onComplete: () => drop.destroy(),
      });
    }
    this.setStatus('Tęcza z fontanny! 🌈');
  }

  private paintFlowers(): void {
    this.flowerLayer?.removeAll(true);
    for (const f of this.flowers) {
      const key = flowerTextureKey(f.kind);
      ensureGardenTexture(this, key);
      const t = this.add.image(f.x, f.y, key).setDisplaySize(40, 40);
      this.flowerLayer?.add(t);
    }
  }

  private paletteLayer: Phaser.GameObjects.Container | null = null;

  private paintFlowerPalette(): void {
    this.paletteLayer?.destroy(true);
    this.paletteLayer = this.add.container(0, 0).setDepth(20_050);
    this.paletteLayer.setScrollFactor(0);
    this.root?.add(this.paletteLayer);

    const y = this.gameH - 70;
    const startX = 360;
    FLOWER_CATALOG.forEach((f, i) => {
      const x = startX + i * (MIN_TOUCH + 12);
      const selected = this.selectedFlower === f.kind;
      const bg = this.add
        .rectangle(x, y, MIN_TOUCH, MIN_TOUCH, selected ? 0x80deea : 0xfff8e1)
        .setStrokeStyle(selected ? 4 : 2, selected ? 0x00838f : 0xffffff)
        .setInteractive({ useHandCursor: true });
      ensureGardenTexture(this, f.texture);
      const icon = this.add.image(x, y, f.texture).setDisplaySize(40, 40);
      bg.on('pointerdown', (p: Phaser.Input.Pointer) => {
        p.event?.stopPropagation?.();
        this.selectedFlower = f.kind;
        this.plantMode = true;
        this.paintFlowerPalette();
        this.setStatus(`Sadzimy: ${f.namePl} — stuknij pole`);
      });
      this.paletteLayer?.add([bg, icon]);
    });
    this.plantMode = true;
  }

  private spawnButterflies(): void {
    ensureGardenTexture(this, 'obj_garden_butterfly');
    const spots = [
      { x: 400, y: 200 },
      { x: 900, y: 180 },
      { x: 1400, y: 220 },
      { x: 1600, y: 240 },
    ];
    for (const s of spots) {
      const b = this.add
        .image(s.x, s.y, 'obj_garden_butterfly')
        .setDisplaySize(40, 40)
        .setInteractive({ useHandCursor: true })
        .setDepth(40);
      b.on('pointerdown', () => {
        this.tweens.add({
          targets: b,
          scale: 1.4,
          alpha: 0,
          duration: 350,
          onComplete: () => {
            b.setPosition(
              200 + Math.random() * (GARDEN_WORLD_W - 400),
              160 + Math.random() * 120,
            );
            b.setAlpha(1);
            b.setScale(1);
          },
        });
        this.catchButterflyAt(s.x, s.y);
      });
      this.tweens.add({
        targets: b,
        y: s.y - 12,
        duration: 900 + Math.random() * 400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.butterflies.push(b);
      this.root?.add(b);
    }
  }

  private setStatus(msg: string): void {
    this.statusText?.setText(msg);
  }
}

export default GardenScene;
